import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "convex/react";
import { useQuery as useReactQuery, useQueryClient } from "@tanstack/react-query";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { Vide } from "@/shared/ui";
import { Loader2 } from "@/shared/ui/icons";

import { api } from "@/../convex/_generated/api";
import type { Id } from "@/../convex/_generated/dataModel";
import { api as cyranoApi } from "@/core/api/client";
import { useSession } from "@/core/auth/useSession";
import { usePermissions } from "@/core/auth/usePermissions";
import { isMobileSync } from "@/core/hooks";
import { layoutAtom, mobileActiveInstanceIdAtom } from "@/core/state";
import {
  addPanel,
  findGroupContaining,
  listAllPanelInstances,
  setActiveTab,
} from "@/shell/layout";
import {
  dismissedNoCreatorWizardAtom,
  dismissedOnboardingSlugAtom,
  requestCreatorWizardAtom,
  requestedOnboardingSlugAtom,
  wizardPanelMountAtom,
} from "@/panels/creators/atoms";
import {
  useCreatorOnboardingState,
  type CreatorOnboardingState,
  type CreatorOnboardingStepName,
} from "@/panels/creators/useCreatorOnboardingState";

import { wizardRoleGate } from "./wizardGate";
import { WizardChrome, WIZARD_STEPS, type WizardStep } from "./WizardChrome";
import { IdentityStep, type IdentityStepHandle } from "./steps/IdentityStep";
import {
  TelegramConnectStep,
  type TelegramConnectStepHandle,
} from "./steps/TelegramConnectStep";
import { TelegramGroupStep, type TelegramGroupStepHandle } from "./steps/TelegramGroupStep";

/**
 * Wizard d'onboarding d'un créateur. Toujours rendu dans le panneau
 * Créateurs — si ce panneau n'est pas dans le workspace courant, on
 * l'auto-ouvre/active avant de monter le wizard (cf useEffect `wantsToShow`
 * plus bas). Le rendu shell-level absolute inset-0 historique est supprimé
 * (feedback user 2026-06-17 : "ça doit se mettre dans un panneau").
 *
 * Toujours fermable via la croix du header ou le bouton "Annuler" du footer.
 * En mode `in-progress`, la fermeture mémorise le slug dans
 * `dismissedOnboardingSlug` : le wizard ne re-popup pas tout seul, l'user
 * revient via le bouton "Reprendre" sur la card pending du panneau Créateurs.
 *
 * S'ouvre automatiquement quand :
 *
 *   - L'agence a fini son onboarding (`accounts.onboardingCompletedAt !== null`)
 *   - ET (creatorsCount === 0 OU premier creator non-complete)
 *
 * Le superadmin bypasse tout (il voit l'usage normal du shell).
 *
 * Le wizard pilote 3 étapes (refonte 2026-05-31 — l'onboarding = création du
 * créateur uniquement) :
 *
 *   1. identity         — form photo (optionnelle) + nom + couleur → crée le creator
 *   2. telegram         — combiné : bot token (PUT /telegram-integration) +
 *                         ≥ 1 compte MTProto (AddTelegramUserAccountModal)
 *   3. telegram_group   — invite bot to group → bot sends inline button → click confirms
 *
 * L'ACTIVATION de Cyrano ne fait PLUS partie de l'onboarding : elle vit dans
 * l'onglet Créateur → Agent (`AgentTab`). Le cockpit se débloque dès que ces
 * 3 étapes sont done — un usage CRM-only sans Cyrano est donc possible.
 * Persona, contenu (médias) et enrichissement ne bloquent pas non plus
 * l'onboarding ; ils restent éditables hors wizard (tab Creator + panel
 * Vault). La connexion Dropp n'est pas un prérequis à la création d'un
 * créateur (les 20% ne gatent que l'agent Cyrano côté pipeline).
 *
 * Navigation :
 *   - Next : exécute `save()` du step courant. Si OK → avance.
 *   - Previous : reviens au step précédent (sans save).
 *
 * Le state machine backend (`/api/creators/{id}/onboarding-state`) reste la
 * source de vérité — le wizard ne fait que driver la UI. Le step "viewé"
 * démarre sur `state.current_step` puis suit le user (Previous/Next).
 */

type CyranoCreatorIdMap = Record<string, string>;

async function fetchCreatorIdMap(): Promise<CyranoCreatorIdMap> {
  const resp = await cyranoApi.get<{ creators: { id: string; slug: string }[] }>(
    "/api/creators",
  );
  const out: CyranoCreatorIdMap = {};
  for (const c of resp.creators ?? []) out[c.slug] = c.id;
  return out;
}

/** Hook combinant onboarding state Convex + creator Convex + onboarding
 *  state Cyrano. Retourne un objet { mode, ... } qui décrit ce que le
 *  wizard doit afficher. */
type WizardData =
  | { mode: "hidden" }
  | { mode: "loading" }
  | {
      mode: "no-creator";
      accountId: Id<"accounts"> | null;
    }
  | {
      mode: "in-progress";
      creatorConvexId: string;
      cyranoCreatorId: string;
      creatorSlug: string;
      creatorDisplayName: string;
      currentColor: string | null;
      currentAvatarUrl: string | null;
      state: CreatorOnboardingState;
    };

function useWizardData(
  requestedNew: boolean,
  pinnedSlug: string | null,
  dismissedSlug: string | null,
  dismissedNoCreator: boolean,
): WizardData {
  const { user } = useSession();
  const { permissions: myPermissions } = usePermissions();
  const onboarding = useQuery(api.onboarding.getMyOnboardingState);
  const isAuthenticated = user !== null;

  const { data: idMap } = useReactQuery({
    queryKey: ["creators", "id-map"],
    queryFn: fetchCreatorIdMap,
    enabled: isAuthenticated,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const accountId = onboarding?.accountId ?? null;
  const creators = useQuery(
    api.creators.listForAccount,
    accountId ? { accountId } : "skip",
  );

  // Target = soit le slug pinné par le wizard (creator just created OU
  // creator actif), soit le premier non-archivé (cas initial 1 creator).
  const targetCreator = pinnedSlug
    ? creators?.find((c) => c.slug === pinnedSlug) ?? null
    : creators?.find((c) => !c.archivedAt) ?? null;
  const targetCyranoId = targetCreator && idMap
    ? idMap[targetCreator.slug] ?? null
    : null;
  // Poll 30s par défaut, 3s pendant l'étape telegram_group — on attend le
  // webhook `my_chat_member` qui peut arriver à tout moment pendant que
  // l'user ajoute le bot au groupe.
  const [pollMs, setPollMs] = useState(30_000);
  const { data: creatorState } = useCreatorOnboardingState(targetCyranoId, pollMs);
  useEffect(() => {
    setPollMs(creatorState?.current_step === "telegram_group" ? 3_000 : 30_000);
  }, [creatorState?.current_step]);

  // Superadmin bypass — voit le cockpit normalement (peut inspecter
  // n'importe quelle agence sans être bloqué par un wizard d'un creator
  // incomplet). MAIS si le superadmin clique explicitement "Ajouter un
  // créateur" (requestedNew=true) OU est en train d'onboarder un creator
  // qu'il vient de créer (pinnedSlug set), on laisse le wizard s'ouvrir.
  const superadminWantsWizard =
    user?.isSuperadmin === true && (requestedNew || pinnedSlug !== null);
  if (user?.isSuperadmin && !superadminWantsWizard) {
    return { mode: "hidden" };
  }

  // Gate RBAC (2026-06-18) : seuls les rôles porteurs de `creators.create`
  // (CEO/Manager) sont poussés dans le wizard — qui CRÉE un créateur. Sans ça,
  // un Chatter/VA/Operator d'une agence à 0 créateur était basculé en mode
  // `no-creator` → auto-open forcé du panneau Créateurs (qu'un Chatter ne peut
  // même pas voir) au lieu du bureau, en boucle à chaque réouverture. Distinct
  // du garde `hasAnyCreator` plus bas (proprio multi-org à 1re org vide) : ici
  // c'est le RÔLE qui n'a pas le droit de créer, quel que soit le nombre de
  // créateurs. Décision pure extraite + testée dans `wizardGate.ts`. `loading`
  // tant que les perms ne sont pas résolues → ne flashe ni le wizard ni
  // l'auto-open du panneau.
  const roleGate = wizardRoleGate(user?.isSuperadmin === true, myPermissions);
  if (roleGate === "loading") return { mode: "loading" };
  if (roleGate === "blocked") return { mode: "hidden" };

  // Auth/loading not ready.
  if (onboarding === undefined) return { mode: "loading" };

  // Pas d'account ou onboarding agency non-fini → laisse OrgGate gérer.
  // Exception superadmin : l'agence parent Dozka n'a typiquement pas
  // d'`onboardingCompletedAt` (le superadmin bypass l'OnboardingScreen via
  // OrgGate dès qu'il a un account), donc on saute ce verrou quand il
  // demande explicitement le wizard. Sinon "Ajouter un créateur" ne ferait
  // rien à l'écran pour un superadmin.
  if (!superadminWantsWizard && (!onboarding.hasAccount || !onboarding.onboardingCompletedAt)) {
    return { mode: "hidden" };
  }
  // Cas superadmin sans account : impossible de cibler une agence pour
  // créer le creator → on garde le wizard fermé.
  if (superadminWantsWizard && !onboarding.hasAccount) {
    return { mode: "hidden" };
  }

  // 0 creator OU user a explicitement demandé la création d'un nouveau
  // creator depuis le bouton "Ajouter un créateur" SANS qu'un slug pinné
  // existe déjà (sinon on est en train d'onboarder le nouveau creator).
  if (
    (onboarding.creatorsCount === 0 || requestedNew) &&
    !pinnedSlug
  ) {
    // Auto-ouverture (creatorsCount===0 SANS clic explicite) : on NE harcèle
    // PAS un proprio multi-org qui a déjà des créateurs dans une AUTRE de ses
    // orgs (cf `hasAnyCreator`, calculé cross-account côté serveur). Le wizard
    // "premier créateur" n'a de sens que pour un user réellement vierge. Bug
    // rapporté 2026-06-18 : le proprio de la marmite (membre aussi de Xeyla,
    // créateurs déménagés) voyait "Créer un créateur" à chaque ouverture car
    // sa 1re org est vide. "Ajouter un créateur" (requestedNew) court-circuite
    // ce garde pour permettre la création explicite.
    if (!requestedNew && onboarding.hasAnyCreator) {
      return { mode: "hidden" };
    }
    // L'user a explicitement fermé un wizard "no-creator" (croix ou Annuler
    // alors qu'aucun créateur n'existait — typiquement après suppression du
    // créateur en cours d'onboarding). On respecte la fermeture jusqu'à ce
    // qu'il clique "Ajouter un créateur" (requestedNew clear le flag dans
    // un useEffect côté composant top-level).
    if (dismissedNoCreator && !requestedNew) {
      return { mode: "hidden" };
    }
    // La connexion Dropp n'est plus un prérequis à la création d'un créateur
    // (refonte 2026-05-30) : un usage CRM-only (chat, flows) ne nécessite pas
    // Dropp. Les 20% restent requis uniquement côté pipeline pour l'offre de
    // contenu payant de l'agent Cyrano.
    return { mode: "no-creator", accountId };
  }

  // 1+ creators mais data pas encore prête.
  if (
    creators === undefined ||
    !targetCreator ||
    idMap === undefined
  ) {
    return { mode: "loading" };
  }

  // Le slug Convex existe mais pas encore propagé à Neon → état transient.
  if (!targetCyranoId) return { mode: "loading" };

  // L'onboarding state n'est pas encore arrivé → loading.
  if (!creatorState) return { mode: "loading" };

  // Creator complete → cockpit débloqué.
  if (creatorState.is_complete) return { mode: "hidden" };

  // L'user a explicitement fermé le wizard pour CE créateur → on respecte
  // la fermeture. Le créateur reste "pending" dans le panneau et l'user peut
  // ré-ouvrir le wizard via le bouton "Reprendre" sur sa card.
  if (dismissedSlug !== null && dismissedSlug === targetCreator.slug) {
    return { mode: "hidden" };
  }

  return {
    mode: "in-progress",
    creatorConvexId: targetCreator._id,
    cyranoCreatorId: targetCyranoId,
    creatorSlug: targetCreator.slug,
    creatorDisplayName: targetCreator.displayName,
    currentColor: targetCreator.color ?? null,
    currentAvatarUrl: targetCreator.avatarUrl ?? null,
    state: creatorState,
  };
}

type StepHandle =
  | IdentityStepHandle
  | TelegramConnectStepHandle
  | TelegramGroupStepHandle;

export function CreatorOnboardingWizard() {
  const [requestedNew, setRequestedNew] = useAtom(requestCreatorWizardAtom);
  const [dismissedSlug, setDismissedSlug] = useAtom(dismissedOnboardingSlugAtom);
  const [dismissedNoCreator, setDismissedNoCreator] = useAtom(
    dismissedNoCreatorWizardAtom,
  );
  const [requestedSlug, setRequestedSlug] = useAtom(requestedOnboardingSlugAtom);
  // Slug du creator actuellement onboardé. Set quand l'user complète step 1
  // depuis le mode `no-creator` (pour qu'on cible CE creator au lieu du
  // premier de la liste, qui peut être un ancien creator déjà complet).
  const [pinnedSlug, setPinnedSlug] = useState<string | null>(null);
  const data = useWizardData(requestedNew, pinnedSlug, dismissedSlug, dismissedNoCreator);

  const panelMount = useAtomValue(wizardPanelMountAtom);
  const setLayout = useSetAtom(layoutAtom);
  const setMobileActive = useSetAtom(mobileActiveInstanceIdAtom);

  // Le bouton "Reprendre l'onboarding" sur la card pending pose
  // `requestedOnboardingSlug`. On le consomme ici : on clear les deux dismiss,
  // on pin le slug, puis on reset l'atom pour redevenir idempotent.
  useEffect(() => {
    if (requestedSlug === null) return;
    setPinnedSlug(requestedSlug);
    setDismissedSlug(null);
    setDismissedNoCreator(false);
    setRequestedSlug(null);
  }, [requestedSlug, setDismissedSlug, setDismissedNoCreator, setRequestedSlug]);

  // Click "Ajouter un créateur" alors qu'un dismiss persiste : on clear le
  // dismiss (intention explicite de réouvrir le wizard). Couvre les deux
  // variantes : slug-based (in-progress) et no-creator.
  useEffect(() => {
    if (!requestedNew) return;
    if (dismissedSlug !== null) setDismissedSlug(null);
    if (dismissedNoCreator) setDismissedNoCreator(false);
  }, [
    requestedNew,
    dismissedSlug,
    dismissedNoCreator,
    setDismissedSlug,
    setDismissedNoCreator,
  ]);

  // Ref : passe à true dès qu'on a affiché le wizard une fois ce mount.
  // Sert à décider : si on retombe en `loading` après avoir été visible,
  // on garde l'overlay (sinon l'user verrait clignoter le cockpit en
  // dessous pendant la transition de step 1 vers step 2).
  const hasBeenVisibleRef = useRef(false);
  if (data.mode === "no-creator" || data.mode === "in-progress") {
    hasBeenVisibleRef.current = true;
  }

  // Reset pinnedSlug + hasBeenVisible quand le wizard se ferme (creator
  // complete → mode hidden). Au prochain re-open le wizard repart neuf.
  useEffect(() => {
    if (data.mode === "hidden" && pinnedSlug !== null) {
      setPinnedSlug(null);
    }
    if (data.mode === "hidden") {
      hasBeenVisibleRef.current = false;
    }
  }, [data.mode, pinnedSlug]);

  // Click "Ajouter un créateur" depuis CreatorsPanel quand un autre creator
  // est déjà in-progress : sans ce clear, pinnedSlug reste sur l'ancien et
  // useWizardData L140 (`!pinnedSlug`) court-circuite la bascule en mode
  // `no-creator` → click feels like nothing happens. Le clear force le
  // wizard à rebattre les cartes : identity step pour le nouveau creator.
  useEffect(() => {
    if (requestedNew && pinnedSlug !== null) {
      setPinnedSlug(null);
    }
  }, [requestedNew, pinnedSlug]);

  // Auto-ouvre le panneau Créateurs quand le wizard veut s'afficher mais que
  // ce panneau n'est pas monté dans le layout : sans ça, `WizardChrome` retombait
  // sur un rendu shell-level absolute inset-0 qui couvrait tout le cockpit
  // (TitleBar + BottomBar restants visibles). Symptômes user 2026-06-17 :
  // "ça se met en plein écran, pas dans un panneau, et ça marche pas toujours"
  // (selon que CreatorsPanel était l'onglet actif ou non).
  const wantsToShow = data.mode === "no-creator" || data.mode === "in-progress";
  useEffect(() => {
    if (!wantsToShow) return;
    if (panelMount !== null) return;
    let openedInstanceId: string | null = null;
    setLayout((prev) => {
      const instances = listAllPanelInstances(prev);
      const existing = instances.find((i) => i.panelId === "creators");
      if (existing) {
        openedInstanceId = existing.id;
        const group = findGroupContaining(prev, existing.id);
        if (!group) return prev;
        const idx = group.panels.findIndex((p) => p.id === existing.id);
        if (idx < 0 || group.activeIndex === idx) return prev;
        return setActiveTab(prev, group.id, idx);
      }
      const res = addPanel(prev, "creators", { kind: "active-group" });
      openedInstanceId = res.instanceId;
      return res.layout;
    });
    if (isMobileSync() && openedInstanceId) {
      setMobileActive(openedInstanceId);
    }
  }, [wantsToShow, panelMount, setLayout, setMobileActive]);

  if (data.mode === "hidden") return null;

  // Loading transient : si on a un slug pinné, ou si l'user vient de
  // demander un nouveau creator, ou si le wizard a déjà été visible dans
  // cette session, on garde l'overlay visible avec un spinner pour éviter
  // que le wizard disparaisse brièvement entre les renders (ce qui
  // laisserait apparaître le cockpit en dessous et ressemblerait à un bug
  // "le créateur est créé et le wizard s'est fermé").
  if (data.mode === "loading") {
    if (pinnedSlug !== null || requestedNew || hasBeenVisibleRef.current) {
      return <LoadingOverlay />;
    }
    return null;
  }

  // Tant que le panneau Créateurs n'est pas encore monté (auto-open en cours),
  // on évite le flash plein écran en affichant un loader transient. Le useEffect
  // ci-dessus a déjà déclenché l'ouverture/activation du panneau ; on attend juste
  // que `wizardPanelMountAtom` reçoive le ref.
  if (panelMount === null) {
    return <LoadingOverlay />;
  }

  // no-creator OU in-progress → on rend le wizard.
  return (
    <WizardInner
      data={data}
      onPinSlug={(slug) => {
        setPinnedSlug(slug);
        // Clear l'atom : on a un creator cible, plus besoin du flag.
        setRequestedNew(false);
      }}
      onDismissRequested={() => {
        // Sortie utilisateur. On mémorise la fermeture côté atom adapté :
        //   - in-progress → slug-based, l'user revient via "Reprendre" sur la card,
        //   - no-creator → boolean global, l'user revient via "Ajouter un créateur".
        // Sans le no-creator dismiss, après suppression du créateur en cours
        // d'onboarding (creatorsCount=0), useWizardData retournait en mode
        // "no-creator" et le wizard se rouvrait — clic Annuler sans effet.
        if (data.mode === "in-progress") {
          setDismissedSlug(data.creatorSlug);
        } else if (data.mode === "no-creator") {
          setDismissedNoCreator(true);
        }
        setRequestedNew(false);
        setPinnedSlug(null);
      }}
    />
  );
}

function LoadingOverlay() {
  return (
    <div
      role="status"
      aria-busy="true"
      // bg-canvas (opaque, pas bg-canvas/95) : pendant la transition
      // step 1 → step 2, le cockpit en dessous ne doit PAS être visible.
      // `absolute` (pas `fixed`) pour rester confine a la zone shell — la
      // TitleBar et la BottomBar du frame applicatif restent visibles dessus.
      className="absolute inset-0 z-[200] flex items-center justify-center bg-canvas text-fg"
    >
      <Loader2 className="h-6 w-6 animate-spin text-muted" />
    </div>
  );
}

interface WizardInnerProps {
  data: Extract<WizardData, { mode: "no-creator" | "in-progress" }>;
  /** Appelé après step 1 (identity) quand un creator est créé/identifié,
   *  pour que `CreatorOnboardingWizard` pinne le wizard sur ce slug et
   *  ignore les autres creators existants (ex: vieil creator complet). */
  onPinSlug: (slug: string) => void;
  /** Demande de fermeture manuelle (croix en haut à droite). Le caller
   *  gère le clear des atoms ; WizardInner ne fait que router le clic. */
  onDismissRequested: () => void;
}

function WizardInner({ data, onPinSlug, onDismissRequested }: WizardInnerProps) {
  const queryClient = useQueryClient();

  // Si on est déjà en `in-progress`, on pinne le slug courant pour la suite
  // (sécurité : même si Convex re-order les creators, on garde la cible).
  useEffect(() => {
    if (data.mode === "in-progress") {
      onPinSlug(data.creatorSlug);
    }
  }, [data, onPinSlug]);

  // Étape "viewée" = celle que l'user voit (peut être ≤ state.current_step
  // si l'user a cliqué Previous pour réviser un step déjà done).
  const initialViewedStep: WizardStep =
    data.mode === "in-progress" ? toWizardStep(data.state.current_step) : "identity";
  const [viewedStep, setViewedStep] = useState<WizardStep>(initialViewedStep);

  // Suit le state machine backend : si current_step avance pendant que
  // l'user reste sur un step précédent done, on ne touche pas (l'user veut
  // peut-être revenir en arrière). Mais si le state machine recule (rare),
  // on aligne. Idem si l'user était sur un step plus avancé que current_step.
  const lastBackendStep = useRef<CreatorOnboardingStepName | null>(null);
  useEffect(() => {
    if (data.mode !== "in-progress") return;
    const backend = data.state.current_step;
    if (lastBackendStep.current === backend) return;
    lastBackendStep.current = backend;
    // Au mount initial : aligne sur le step backend.
    // Sur les ticks suivants : si l'user est sur un step "futur" (pas encore
    // possible), recule sur le backend. Sinon (user en train de réviser un
    // step done), ne touche pas.
    if (backend === "complete") return;
    const backendIdx = WIZARD_STEPS.indexOf(toWizardStep(backend));
    const viewedIdx = WIZARD_STEPS.indexOf(viewedStep);
    if (viewedIdx > backendIdx) setViewedStep(toWizardStep(backend));
  }, [data, viewedStep]);

  const handleRef = useRef<StepHandle | null>(null);
  const [handleVersion, setHandleVersion] = useState(0);
  const onHandle = useCallback((h: StepHandle) => {
    handleRef.current = h;
    setHandleVersion((v) => v + 1);
  }, []);
  // Re-render le footer quand le handle change (isValid/isBusy).
  void handleVersion;

  const [transitionError, setTransitionError] = useState<string | null>(null);
  const [isAdvancing, setIsAdvancing] = useState(false);

  const handle = handleRef.current;
  const canGoNext = handle ? handle.isValid : false;
  const canGoPrevious =
    WIZARD_STEPS.indexOf(viewedStep) > 0 && !isAdvancing;

  async function onNext() {
    if (!handle) return;
    setTransitionError(null);
    setIsAdvancing(true);
    try {
      const ok = await handle.save();
      if (!ok) return;
      // Invalidate l'onboarding state pour pull les counters frais.
      await queryClient.invalidateQueries({
        queryKey: ["creator-onboarding-state"],
      });
      const idx = WIZARD_STEPS.indexOf(viewedStep);
      if (idx < WIZARD_STEPS.length - 1) {
        setViewedStep(WIZARD_STEPS[idx + 1]!);
      }
    } catch (err) {
      setTransitionError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsAdvancing(false);
    }
  }

  function onPrevious() {
    const idx = WIZARD_STEPS.indexOf(viewedStep);
    if (idx > 0) setViewedStep(WIZARD_STEPS[idx - 1]!);
    setTransitionError(null);
  }

  return (
    <WizardChrome
      mode="solid"
      state={data.mode === "in-progress" ? data.state : null}
      viewedStep={viewedStep}
      creatorDisplayName={
        data.mode === "in-progress" ? data.state.identity.display_name : null
      }
      canGoNext={canGoNext}
      canGoPrevious={canGoPrevious}
      onPrevious={onPrevious}
      onNext={onNext}
      nextBusy={isAdvancing || handle?.isBusy === true}
      // Croix + bouton Annuler toujours dispos (refonte 2026-06-17 — feedback
      // user "on ne peut pas annuler"). En `in-progress`, la fermeture mémorise
      // le slug dans `dismissedOnboardingSlug` côté parent : le créateur reste
      // "pending" dans le panneau et l'user peut revenir via "Reprendre".
      onClose={onDismissRequested}
    >
      {/* StepContent monte toujours pour que la step register son handle
          via onValidityChange (le bouton Suivant en footer reste piloté
          par handle.isValid). */}
      <StepContent
        viewedStep={viewedStep}
        data={data}
        onHandle={onHandle}
        externalError={transitionError}
        onSlugPinned={onPinSlug}
      />
    </WizardChrome>
  );
}

function StepContent({
  viewedStep,
  data,
  onHandle,
  externalError,
  onSlugPinned,
}: {
  viewedStep: WizardStep;
  data: Extract<WizardData, { mode: "no-creator" | "in-progress" }>;
  onHandle: (h: StepHandle) => void;
  externalError: string | null;
  onSlugPinned: (slug: string) => void;
}) {
  // Step 1 (identity) — toujours rendable (avec ou sans creator existant).
  if (viewedStep === "identity") {
    return (
      <IdentityStep
        currentCreatorSlug={
          data.mode === "in-progress" ? data.creatorSlug : null
        }
        currentDisplayName={
          data.mode === "in-progress" ? data.state.identity.display_name : null
        }
        currentColor={
          data.mode === "in-progress" ? data.currentColor : null
        }
        currentAvatarUrl={
          data.mode === "in-progress" ? data.currentAvatarUrl : null
        }
        onValidityChange={onHandle}
        errorMessage={externalError}
        isSyncingToNeon={false}
        onSlugPinned={onSlugPinned}
      />
    );
  }

  // À partir du step 2, on a forcément un creator (sinon le wizard force
  // l'user à rester sur identity via le state machine backend).
  if (data.mode !== "in-progress") {
    return (
      <Vide titre="Veuillez d'abord créer un créateur (étape 1)." />
    );
  }

  const { state, cyranoCreatorId, creatorSlug } = data;

  switch (viewedStep) {
    case "telegram":
      return (
        <TelegramConnectStep
          cyranoCreatorId={cyranoCreatorId}
          creatorSlug={creatorSlug}
          currentBotUsername={state.telegram.bot_username}
          currentBotFirstName={state.telegram.bot_first_name}
          onValidityChange={onHandle}
          errorMessage={externalError}
        />
      );
    case "telegram_group":
      return (
        <TelegramGroupStep
          cyranoCreatorId={cyranoCreatorId}
          telegramGroupState={state.telegram_group}
          botUsername={state.telegram.bot_username}
          onValidityChange={onHandle}
          externalError={externalError}
        />
      );
    default:
      return null;
  }
}

function toWizardStep(step: CreatorOnboardingStepName): WizardStep {
  // "complete" ne devrait jamais être rendu (le wizard se ferme alors), mais on
  // mappe sur la dernière étape par sécurité.
  if (step === "complete") return "telegram_group";
  return step;
}
